import axios, { AxiosResponse } from 'axios';
import { apiClient } from './endpoints';
import { DailyMachine, DailyRental, DailySubContractor, DailyTempWorker, DailyTool } from './planning';
import { User } from './users';
import { DailyTruckPlanned } from './dailyReports/dailyTrucksPlanned';
import { DailyTruckRide } from './dailyReports/dailyTrucksRides';
import { UtilityVehicle } from './materials/utilityVehicles';
import { Truck } from './materials/trucks';
import { DailySupply } from './dailyReports/dailySupplies';
import { WorkTravelZone } from './sites/workTravelZones';
import { ActivityType } from './dailyReports/activityTypes';

export interface DropDownItem {
    label: string;
    value: string;
}

export interface Report {
    _id: string;
    userId: User;
    date: string;
    reportState: number;
    reportType: number;
    fuel?: number;
    km?: number;
    gnr?: number;
    reportSubmitValidationTimeStamp?: string;
    validatedTimeStamp?: string;
    vehicleId?: UtilityVehicle;
    truckId?: Truck;
    userPrice?: number;
    userPriceType?: number;
    vehiclePrice?: number;
    vehiclePriceType?: number;
    truckPrice?: number;
    truckPriceType?: number;
    workTravelZoneId?: WorkTravelZone;
    workTravelZonePrice?: number;
    workTime?: number;
    extraHours?: number;
    isWorkTravel?: boolean;
    isLongTrip?: boolean;
    workStartTime?: string;
    workEndTime?: string;
    overTimeValidatedBy?: User;
}

interface Site {
    _id: string;
    codeProC: string;
    name: string;
    stateProC: number;
    conducId?: User;
    assistantConducId?: User;
    address?: string;
    crudStatus: number;
    entityId: string;
    type: number;
    workTravelZoneId: WorkTravelZone;
}

export interface DataItem {
    _id: string;
    activityTypeId: ActivityType;
    reportId: Report;
    activityState: number;
    siteId: Site;
    startTime: string;
    validationTime?: string;
    validationTimestamp?: string;
    observationsPlanning?: string;
    observationsWorker?: string;
    needsValidationTime: string;
    planningValidationTime: string;
    isTruckManager: boolean;
    dailyMachines: DailyMachine[];
    dailyTools: DailyTool[];
    dailyRentals: DailyRental[];
    dailySubContractors: DailySubContractor[];
    dailyTempWorkers: DailyTempWorker[];
    dailyTrucksPlanned: DailyTruckPlanned[];
    dailyTrucksRides: DailyTruckRide[];
    dailySupplies: DailySupply[];
    needSubmitValidationTimeStamp?: string;
    planningSubmitValidationTimeStamp?: string;
    stepMode: number;
    isSpecialHidden?: boolean;
    isHighlight?: boolean;
    includedInExpenses?: boolean;
    isLongDistance?: boolean;
    longDistancePrice?: number;
    hasPendingInfos?: boolean;
    quantity: number;
    unitUserPrice?: number;
    unitVehiclePrice?: number;
    unitLongTripPrice?: number;
}

export interface ActivitiesResponse {
    message: string;
    data: DataItem[];
}

export interface GetActivitiesQueries {
    startDate?: string;
    endDate?: string;
    stepMode?: number;
    userId?: string;
    conducId?: string;
}

export const getActivities = async (queries?: GetActivitiesQueries): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.get('activities', { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getActivitiesByDateAndState = async (
    date: string,
    minState: number,
    maxState: number,
    stepMode: number
): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.get(
            'activities/date/' + date + '/state/' + minState + '/and/' + maxState + '/stepMode/' + stepMode
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const getActivitiesBetweenDateAndState = async (
    startDate: string,
    endDate: string,
    minState: number,
    maxState: number,
    stepMode: number,
    userId: string
): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.get(
            'activities/startDate/' +
                startDate +
                '/endDate/' +
                endDate +
                '/state/' +
                minState +
                '/and/' +
                maxState +
                '/stepMode/' +
                stepMode +
                '/user/' +
                userId
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DailyMachineResponse {
    message: string;
    data: DailyMachine;
}

export interface UpdateDailyMachineBody {
    activityId?: string;
    machineId?: string;
    state?: number;
    createTransfer?: boolean;
    isUsed?: boolean;
    price?: number | null;
    priceType?: number | null;
    quantity?: number;
    unitPrice?: number | null;
}

export interface UpdateDailyMachineQueries {
    deleteTransfers?: boolean;
    syncTransfers?: boolean;
}

export const updateDailyMachine = async (
    id: string,
    body: UpdateDailyMachineBody,
    queries?: UpdateDailyMachineQueries
): Promise<DailyMachineResponse> => {
    try {
        const response: AxiosResponse<DailyMachineResponse> = await apiClient.patch('/daily-machines/' + id, body, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DailyToolResponse {
    message: string;
    data: DailyTool;
}

export interface UpdateDailyToolBody {
    activityId?: string;
    toolId?: string;
    state?: number;
    createTransfer?: boolean;
    isUsed?: boolean;
    price?: number | null;
    priceType?: number | null;
    quantity?: number;
    unitPrice?: number | null;
}

export interface UpdateDailyToolQueries {
    deleteTransfers?: boolean;
    syncTransfers?: boolean;
}

export const updateDailyTool = async (
    id: string,
    body: UpdateDailyToolBody,
    queries?: UpdateDailyToolQueries
): Promise<DailyToolResponse> => {
    try {
        const response: AxiosResponse<DailyToolResponse> = await apiClient.patch('/daily-tools/' + id, body, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DailyRentalResponse {
    message: string;
    data: DailyRental;
}
export interface UpdateDailyRentalBody {
    activityId?: string;
    rentalTypeId?: string;
    state?: number;
    designation?: string;
    createTransfer?: boolean;
    isUsed?: boolean;
    price?: number | null;
    priceType?: number | null;
    observations?: string;
    quantity?: number;
    unitPrice?: number | null;
    isExposedToWeather?: boolean;
}

export interface UpdateDailyRentalQueries {
    deleteTransfers?: boolean;
    syncTransfers?: boolean;
}

export const updateDailyRental = async (
    id: string,
    body: UpdateDailyRentalBody,
    queries?: UpdateDailyRentalQueries
): Promise<DailyRentalResponse> => {
    try {
        const response: AxiosResponse<DailyRentalResponse> = await apiClient.patch('/daily-rentals/' + id, body, { params: queries });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DailySubContractorResponse {
    message: string;
    data: DailySubContractor;
}

export interface UpdateDailySubContractorBody {
    activityId?: string;
    subContractorTypeId?: string;
    durationDay?: number;
    state?: number;
    price?: number | null;
    priceType?: number | null;
    includedInExpenses?: boolean;
}

export const updateDailySubContractor = async (id: string, body: UpdateDailySubContractorBody): Promise<DailySubContractorResponse> => {
    try {
        const response: AxiosResponse<DailySubContractorResponse> = await apiClient.patch('/daily-subcontractors/' + id, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export interface DailyTempWorkerResponse {
    message: string;
    data: DailyTempWorker;
}

export interface UpdateDailyTempWorkerBody {
    activityId?: string;
    tempWorkerTypeId?: string;
    durationDay?: number;
    state?: number;
    durationHours?: number;
    tempWorkerName?: string;
    price?: number | null;
    priceType?: number | null;
    includedInExpenses?: boolean;
    unitPrice?: number | null;
}

export const updateDailyTempWorker = async (id: string, body: UpdateDailyTempWorkerBody): Promise<DailyTempWorkerResponse> => {
    try {
        const response: AxiosResponse<DailyTempWorkerResponse> = await apiClient.patch('/daily-tempworkers/' + id, body);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const importActivitiesByConduc = async (conducId: string, date: string, fromState: number, toState: number): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.patch('/activities/conduc/' + conducId + '/date/' + date + '/state', {
            fromState,
            toState,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const importActivitiesBySite = async (
    siteId: string,
    date: string,
    fromState: number,
    toState: number,
    stepMode: number
): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.patch(
            '/activities/site/' + siteId + '/date/' + date + '/stepMode/' + stepMode + '/state',
            {
                fromState,
                toState,
            }
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const importActivitiesByDate = async (date: string, fromState: number, toState: number): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.patch('/activities/date/' + date + '/state', {
            fromState,
            toState,
        });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const cancelActivities = async (
    date: string,
    state: number,
    stepMode: number,
    newStepMode: number,
    newState: number
): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.patch(
            '/activities/cancel/date/' + date + '/stepMode/' + stepMode + '/state/' + state,
            {
                newStepMode,
                newState,
            }
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const cancelActivity = async (activityId: string, state: number, stepMode: number): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.patch(
            '/activities/cancel/' + activityId + '/stepMode/' + stepMode + '/state/' + state
        );
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};

export const importLastNeeds = async (siteId: string, date: string): Promise<ActivitiesResponse> => {
    try {
        const response: AxiosResponse<ActivitiesResponse> = await apiClient.post('activities/import', { siteId, date });
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
            throw new Error(error.response.data.message);
        } else {
            throw new Error('An unexpected error occurred');
        }
    }
};
